import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { convertLegacyPlugin } from '@backstage/core-compat-api';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import artifactoryPlugin from '@jllt/plugin-artifactory-frontend/alpha';
import { backstagePluginSnykPlugin } from 'backstage-plugin-snyk';
import dockerPlugin from '@jllt/plugin-dev-platform-docker-frontend/alpha';
import securityPlugin from '@jllt/plugin-security-frontend/alpha';
import harnessCheckPlugin from '@jllt/plugin-harness-check-frontend/alpha';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import techdocsAddons from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import rbacPlugin from '@backstage-community/plugin-rbac/alpha';
import announcementsPlugin from '@backstage-community/plugin-announcements/alpha';
import techRadarPlugin from '@backstage-community/plugin-tech-radar/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import signalsPlugin from '@backstage/plugin-signals/alpha';
import devToolsPlugin from '@backstage/plugin-devtools/alpha';
import authPlugin from '@backstage/plugin-auth';
import { navModule } from './modules/nav';
import { homePageModule } from './home';
import {
  adminAuthApiExtension,
  toastApiExtension,
  scmIntegrationsApiExtension,
  scmAuthApiExtension,
} from './apis';
import { signInPageExtension } from './auth/SignInPage';
import { announcementBannerExtension } from './nav';
import { searchPageModule } from './search';
import { entityPageModule } from './entity/entityPage';
import { pluginApisModule } from './entity/pluginApis';
import { catalogPageModule } from './catalog';
import { devToolsPageModule } from './devtools';
import { signalsDisplayExtension } from './signals/SignalsDisplayExtension';
import learningVideosAddonModule from '@jllt/plugin-learning-videos-addon/alpha';
import { jfrogLibsBridgeModule } from './jfrogLibsBridge';

const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    adminAuthApiExtension,
    toastApiExtension,
    scmIntegrationsApiExtension,
    scmAuthApiExtension,
    signInPageExtension,
    announcementBannerExtension,
    signalsDisplayExtension,
  ],
});

/**
 * Legacy (old frontend system) plugins whose entity-tab content is a *routable*
 * extension (`createRoutableExtension`). Their components call `useRouteRef` on
 * a mountPoint route ref, so the route ref must be registered in the app route
 * tree - otherwise the new frontend system throws "Routable extension was not
 * discovered in the app element tree" and fires the dev error overlay when the
 * tab is opened. `convertLegacyPlugin` registers each plugin's routes (and any
 * api factories) on the new frontend system.
 *
 * - Snyk: `EntitySnykContent` (Snyk tab). `convertLegacyPlugin` registers its
 *   `snykApiRef` factory too, so the factory is NOT duplicated in
 *   `entity/pluginApis.ts`.
 *
 * GitHub Actions and Artifactory ship proper `/alpha` features (the Artifactory
 * plugin now contributes its API and entity-content tab directly), so they are
 * added directly.
 */
const snykCompatPlugin = convertLegacyPlugin(backstagePluginSnykPlugin, {
  extensions: [],
});

export default createApp({
  features: [
    catalogPlugin,
    scaffolderPlugin,
    techdocsPlugin,
    techdocsAddons,
    searchPlugin,
    apiDocsPlugin,
    catalogImportPlugin,
    catalogGraphPlugin,
    orgPlugin,
    userSettingsPlugin,
    catalogUnprocessedEntitiesPlugin,
    homePlugin,
    rbacPlugin,
    announcementsPlugin,
    techRadarPlugin,
    notificationsPlugin,
    signalsPlugin,
    devToolsPlugin,
    // @backstage/plugin-auth ships the experimental OAuth 2.1 / DCR consent
    // page at /oauth2/authorize/:sessionId (new frontend system only). It is
    // required to unblock per-user MCP auth (JDP-1429); the old frontend
    // system's plugin-auth-react did not ship this route, so it 404'd.
    authPlugin,
    navModule,
    homePageModule,
    appModule,
    searchPageModule,
    entityPageModule,
    pluginApisModule,
    catalogPageModule,
    devToolsPageModule,
    learningVideosAddonModule,
    jfrogLibsBridgeModule,
    githubActionsPlugin,
    artifactoryPlugin,
    snykCompatPlugin,
    dockerPlugin,
    securityPlugin,
    harnessCheckPlugin,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});
